// THE NAME A PERSON READS FOR AN INSTALLED APP — the App Store's catalogue
// name, in the session language.
//
// Nika 2026-10-23: "app names = what a person sees in the workspace App Store
// ('Catalogue names'). When two surfaces disagree, change the one that
// differs." The persona walks of 2026-10-24 measured the disagreement: the app
// window's apps rail named the installed apps "Store, Catalog, CMS, Analytics"
// (marcus, 390 px) and /add-app's "Installed apps" list read "CMS, CRM,
// Scheduling, Store, Catalog" (aiko, 1512 px), while the App Store, the phone
// Apps sheet and the /add-app cards said Commerce, Products, Content,
// Insights. Those are the words of each app's MANIFEST (`configDefaults.navbar`
// / `.apps`), printed by every surface that took a manifest row's label.
//
// So this is no second naming rule. It hands THE store-copy resolver
// (shared functions/storeItemText.js — the one the App Store itself calls) the
// app's record identity and the words the app already carries, and returns
// what that resolver says: the record's own localized name, else the shipped
// `marketplace.app.<slug>.name`, else the app's own words through polyglot.
// An app the store ships no name for (a third party's, a customer's, an
// extension) keeps its own words, untouched.
//
// `app` is whatever the caller holds that names one installed app:
//   • a `root.workspaceApps` entry (`{ appId: 'owner/key', navbar, apps }`);
//   • a row the shell STAMPED with its app's identity (`{ appKey, label }` /
//     `{ appKey, title }` — workspaceConfigs.js stamps `appKey` on the ONE row
//     an app contributes, and only then);
//   • a plain `{ appId, label }` a caller builds from what it has.
// A row with no identity — a sub-surface of an app that ships several rows
// ("Policies" + "Claims"), a house tile, a customer's own tab — has no record
// to ask, so it reads exactly as before: its own words through polyglot.
//
// Call it as `el.call('installedAppName', app)` — `el.call` binds `this` = el.
// A direct import call (no element) returns the app's own words unresolved.
export const installedAppName = function installedAppName(app) {
  if (!app || typeof app !== 'object') return ''
  const text = (v) => (typeof v === 'string' ? v.trim() : '')
  // The record identity. `appKey` is the stamp a row carries; an id is
  // `owner/key` (`appId` on a loaded entry, `catalogId` on an activation,
  // `id` on a loaded entry and on the takeover module) and the record key is
  // its last segment. An id without a `/` (an extension's project id) names
  // no store record.
  const ownerKey = [app.appId, app.catalogId, app.id].map(text).find((v) => v.includes('/'))
  const key = text(app.appKey) || (ownerKey ? ownerKey.split('/').pop() : '')
  // The words the app already carries: the row's own, else the entry's first
  // navbar tab, else its first launcher tile.
  const nav = (Array.isArray(app.navbar) ? app.navbar : []).find((n) => n && text(n.label))
  const tile = (Array.isArray(app.apps) ? app.apps : []).find(
    (t) => t && (text(t.title) || text(t.label))
  )
  const own =
    text(app.label) ||
    text(app.title) ||
    text(app.name) ||
    (nav ? text(nav.label) : '') ||
    (tile ? text(tile.title) || text(tile.label) : '')
  if (!this || typeof this.call !== 'function') return own
  const named = this.call('storeItemText', { key, name: own }, 'name')
  return typeof named === 'string' ? named : own
}
